import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchConversation,
  type ConversationDetail,
  type ConversationTranscript,
} from '@/lib/api';

export function conversationQueryKey(slug: string, id: string) {
  return ['conversation', slug, id] as const;
}

// Part C (2026-06-05) — de-dupe the transcripts array. A single call was
// surfacing twice in the "Calls · N" sheet (Raquel 4QFN30cf — one real call
// rendered as "Calls · 2" with two identical Jun 6 8:19 AM / 1:56 rows). The
// audit confirmed only ONE call, so the payload carries a duplicate transcript
// entry (e.g. a legacy + v2 SHELBY CALL TRANSCRIPT note for the same call).
// De-dupe here in the hook so BOTH the count chip (transcripts.length in
// ConversationDetail) and the list (CallContextBar) collapse to one. Order is
// preserved (newest-first), so the "LATEST" badge on index 0 stays correct.
function transcriptKey(t: ConversationTranscript): string {
  // Prefer the most call-unique signal available: recording URL, then the
  // call timestamp, then a duration + transcript-prefix composite.
  if (t.recording_url && t.recording_url.trim()) return `rec:${t.recording_url.trim()}`;
  if (t.iso && t.iso.trim()) return `iso:${t.iso.trim()}`;
  return `dt:${t.duration_sec}:${(t.text ?? '').slice(0, 80)}`;
}

function dedupeTranscripts(list: ConversationTranscript[] | undefined): ConversationTranscript[] | undefined {
  if (!Array.isArray(list) || list.length < 2) return list;
  const seen = new Set<string>();
  const out: ConversationTranscript[] = [];
  for (const t of list) {
    const k = transcriptKey(t);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out.length === list.length ? list : out;
}

export function useConversation(
  slug: string,
  id: string | undefined,
): UseQueryResult<ConversationDetail> {
  return useQuery<ConversationDetail>({
    queryKey: conversationQueryKey(slug, id ?? ''),
    queryFn: () => {
      if (!id) throw new Error('Conversation id required');
      return fetchConversation(id, slug);
    },
    enabled: Boolean(slug && id),
    // Conversation pane is the live screen — keep it fresh.
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    // De-dupe duplicate transcript entries before they reach the UI.
    select: (data) =>
      data && data.transcripts
        ? { ...data, transcripts: dedupeTranscripts(data.transcripts) }
        : data,
  });
}
